import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import SimulationModal from './modals/simulations/simulationModal';
import DeleteSimulationModal from './modals/simulations/deleteSimulationModal';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';

const SimulationsTable = ({ items, onToggle, sortConfig, onSortChange, fetchSimulations, setSimulations }) => {

  const handleUpdate = (simulation) =>  {    
    toast.success(`${simulation.label} Updated`, { autoClose: 3000 });
    fetchSimulations();
  };

  const handleDelete = (simulation) =>  {
    setSimulations(items.filter(items => items.id !== simulation.id));
    toast.success(`${simulation.label} Deleted`, { autoClose: 3000 });
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Attack Type', 'Sequence', 'Description', 'Media', 'Active'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', simulationSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.label}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.sequence}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.description}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.media}</TableCell>
                <TableCell> <Switch checked={!!item.active} onChange={(e) => onToggle(item, 'active', e.target.checked)} /> </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <SimulationModal simulation={ item } onTriggerUpdate={handleUpdate} />
                        <DeleteSimulationModal simulation={ item } onTriggerDelete={handleDelete} />
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function Simulations() {
    const [simulations, setSimulations] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchSimulations();
    }, []);

    const fetchSimulations = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/simulator/`);            
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setSimulations(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (simulation) =>  {
      toast.success(`${simulation.attack_type} Simulation Created`, { autoClose: 3000 });
      fetchSimulations();
    };


    const handleUpdate = async (simulation) => {
         const res = await fetch(`${process.env.REACT_APP_API}/simulator/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(simulation)
         });
         if (!res.ok) {
            toast.error(`Failed to update simulation : ${res.status}`);
          } else {
            setSimulations((prev) => prev.map((p) => (p.id === simulation.id ? simulation : p)));
         
            if ( simulation.active ) {
              toast.success(`${simulation.label} Simulation Has Been Activated`, { autoClose: 3000 });
            } else {
              toast.success(`${simulation.label} Simulation Has Been De-Activated`, { autoClose: 3000 });
            }
            
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = simulations?.filter((item) => {
        const s = String(searchQuery.toLowerCase());
        return ['label', 'sequence', 'description', 'media'].some((field) => String(item[field])?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Simulations" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Simulations..."
                      label="Search Simulations..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <SimulationsTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchSimulations={fetchSimulations}
                    setSimulations={setSimulations}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <SimulationModal simulation='' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}
